/**
 * Forge Memory Engine · React hooks + API client
 * Talks to /api/memory/* endpoints. No auth required.
 */
import { useCallback, useEffect, useState } from "react";

const API = "";

async function req(path, opts = {}) {
  const r = await fetch(`${API}/api/memory${path}`, {
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`HTTP ${r.status}: ${text}`);
  }
  return r.json();
}

export const memoryApi = {
  status: () => req("/status"),
  stats: () => req("/stats"),
  load: (agent = "aria", format = "structured") =>
    req(`/load?agent=${agent}&format=${format}`),
  search: (body) => req("/search", { method: "POST", body: JSON.stringify(body) }),
  propose: (body) => req("/propose", { method: "POST", body: JSON.stringify(body) }),
  confirm: (id, agent = "user") =>
    req(`/confirm/${id}`, { method: "POST", body: JSON.stringify({ agent }) }),
  reject: (id, agent = "user") =>
    req(`/reject/${id}`, { method: "POST", body: JSON.stringify({ agent }) }),
  setRule: (target, category, rule, agent = "user") =>
    req("/rule", { method: "POST", body: JSON.stringify({ target, category, rule, agent }) }),
  pending: (limit = 30) => req(`/proposals/pending?limit=${limit}`),
  list: (collection, limit = 30) => req(`/list/${collection}?limit=${limit}`),
  get: (collection, id) => req(`/get/${collection}/${id}`),
  consolidate: () => req("/consolidate", { method: "POST", body: "{}" }),
  backup: (agent = "user") =>
    req("/backup", { method: "POST", body: JSON.stringify({ agent }) }),
  backups: () => req("/backups"),
  restore: (path, dryRun = false, agent = "user") =>
    req("/restore", { method: "POST", body: JSON.stringify({ path, dry_run: dryRun, agent }) }),
  integrity: () => req("/integrity"),
  deleteDoc: (collection, id, reason, agent = "user") =>
    req(`/doc/${collection}/${id}`, {
      method: "DELETE",
      body: JSON.stringify({ reason, confirm: true, agent }),
    }),
};

export function useMemoryStats(pollMs = 30000) {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const refresh = useCallback(async () => {
    try {
      const s = await memoryApi.stats();
      setStats(s);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, []);
  useEffect(() => {
    refresh();
    if (pollMs > 0) {
      const t = setInterval(refresh, pollMs);
      return () => clearInterval(t);
    }
  }, [refresh, pollMs]);
  return { stats, error, refresh };
}

export function usePendingProposals(pollMs = 15000) {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    try {
      const d = await memoryApi.pending();
      setProposals(d.proposals || []);
    } catch (e) {
      setProposals([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    refresh();
    if (pollMs > 0) {
      const t = setInterval(refresh, pollMs);
      return () => clearInterval(t);
    }
  }, [refresh, pollMs]);
  return { proposals, loading, refresh };
}
